import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { RestService } from '../../Services/rest.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import RFB from "../../../../node_modules/@novnc/novnc/core/rfb.js";
import { environment } from 'src/environments/environment';
import { RecorderSessionService, LabInfo } from '../../Services/recorder-session.service';
import { RecordedStepsService } from '../../Services/recorded-steps.service';
import { StepSaveService } from '../../Services/step-save.service';

interface ResolutionOption {
  value: string;   // "WIDTHxHEIGHT", sent to the backend as-is
  width: number;
  height: number;
  label: string;   // "1920 x 1080 (Full HD)"
}

const RESOLUTION_PRESETS: ResolutionOption[] = [
  { value: '1366x768',  width: 1366, height: 768,  label: '1366 x 768 (Laptop)' },
  { value: '1440x900',  width: 1440, height: 900,  label: '1440 x 900' },
  { value: '1536x864',  width: 1536, height: 864,  label: '1536 x 864' },
  { value: '1600x900',  width: 1600, height: 900,  label: '1600 x 900' },
  { value: '1920x1080', width: 1920, height: 1080, label: '1920 x 1080 (Full HD)' },
  { value: '2560x1440', width: 2560, height: 1440, label: '2560 x 1440 (QHD)' },
];

@Component({
  selector: 'ze-web-recorder-session',
  templateUrl: './web-recorder-session.component.html',
  styleUrls: ['./web-recorder-session.component.scss']
})
export class WebRecorderSessionComponent implements OnInit, OnDestroy {

  labs: LabInfo[] = [];
  selectedLab: LabInfo | null = null;
  isLoadingLabs = false;
  labLoadError = '';

  resolutionOptions: ResolutionOption[] = RESOLUTION_PRESETS;
  recommendedResolution = '';
  selectedResolution = '';

  public rfb: any;
  private heartbeatInterval: any;
  private vncRescaleTimeout: any;

  // Single URL field — used for session creation AND navigation
  sessionUrl = '';

  isCreatingSession = false;
  isClosingSession = false;

  isUploading = false;
  uploadStatus: 'idle' | 'success' | 'error' = 'idle';
  selectedFile: File | null = null;

  safeVncUrl: SafeResourceUrl | null = null;

  // "Session Details" panel (Lab/Navigate/session buttons) — collapsible so
  // it doesn't permanently eat space in the narrow sidebar once a session
  // is running and the step list needs the room.
  sessionDetailsExpanded = true;

  toggleSessionDetails(): void {
    this.sessionDetailsExpanded = !this.sessionDetailsExpanded;
  }

  // Interacting with steps (selecting one, recording, etc.) means the user
  // is done with session setup — free up the vertical space automatically
  // instead of making them collapse it by hand every time.
  collapseSessionDetails(): void {
    if (this.sessionDetailsExpanded) this.sessionDetailsExpanded = false;
  }

  constructor(
    private restService: RestService,
    private sanitizer: DomSanitizer,
    public recorderSession: RecorderSessionService,
    private steps: RecordedStepsService,
    private stepSave: StepSaveService
  ) {}

  ngOnInit(): void {
    this.loadLabs();

    // "Recommended" badge still points at whatever's closest to the user's
    // own screen, but the pre-selected default is always Full HD — matches
    // what most lab machines are actually provisioned at, regardless of the
    // browser window this page happens to be viewed from.
    const closest = this.resolutionOptions.reduce((best, option) => {
      const dist = Math.abs(option.width - window.screen.width) + Math.abs(option.height - window.screen.height);
      const bestDist = Math.abs(best.width - window.screen.width) + Math.abs(best.height - window.screen.height);
      return dist < bestDist ? option : best;
    });
    this.recommendedResolution = closest.value;
    this.selectedResolution = '1920x1080';
  }

  ngOnDestroy(): void {
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    if (this.vncRescaleTimeout) clearTimeout(this.vncRescaleTimeout);
    this.disconnectVnc();
  }

  // noVNC's own ResizeObserver-driven rescale (scaleViewport) doesn't
  // reliably recompute against this layout's grid/flex container once it's
  // already connected — the canvas stays pinned to whatever size it first
  // connected at until a fresh RFB connection is made (e.g. on page
  // refresh). Re-assigning `scaleViewport` forces noVNC to synchronously
  // recompute the fit-to-container scale against the CURRENT container
  // size (its setter calls _updateScale() directly), so we do that
  // ourselves on window resize instead of relying on noVNC to notice.
  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.rfb) return;
    clearTimeout(this.vncRescaleTimeout);
    this.vncRescaleTimeout = setTimeout(() => {
      if (this.rfb) this.rfb.scaleViewport = true;
    }, 150);
  }

  // Every /recorderSession endpoint is now @FeatureGated(entityIdVariable /
  // bodyProjectIdField = "projectId") on ataserver — matches the same
  // localStorage key already used app-wide (see execute.service.ts,
  // step-service.service.ts) for the current project's id.
  private getProjectId(): string {
    return localStorage.getItem('projectIdDb2') || '';
  }

  // ─────────────────────────────────────────────────────────────────────
  // LABS
  // ─────────────────────────────────────────────────────────────────────

  loadLabs(): void {
    this.isLoadingLabs = true;
    this.labLoadError = '';

    this.restService.get('getRecorderLabs', this.getProjectId()).subscribe({
      next: (res: any) => {
        this.labs = res.data || [];
        if (this.labs.length > 0) this.selectedLab = this.labs[0];
        this.isLoadingLabs = false;
        this.pingSession();
      },
      error: (err: any) => {
        console.error('Failed to load labs:', err);
        this.labLoadError = 'Could not load labs. Please refresh.';
        this.isLoadingLabs = false;
      }
    });
  }

  selectLab(lab: LabInfo): void {
    this.selectedLab = lab;
  }

  get selectedLabHost(): string {
    return this.selectedLab.labHost || '';
  }

  // ─────────────────────────────────────────────────────────────────────
  // SESSION
  // ─────────────────────────────────────────────────────────────────────

  pingSession(): void {
    this.restService.get('pingRecorderSession', this.getProjectId()).subscribe({
      next: (res: any) => {
        const session = res.data;

        if (session && session.session_id) {
          this.recorderSession.sessionId  = session.session_id;
          this.recorderSession.vncUrl     = session.vnc_url || null;
          this.sessionUrl = session.website_url || '';

          if (session.lab_ip_address && this.labs.length > 0) {
            const match = this.labs.find(
              (l: LabInfo) => l.ipAddress === session.lab_ip_address
              || l.labHost === session.lab_ip_address
            );
            if (match) this.selectedLab = match;
          }

          setTimeout(() => {
            this.startVncClient(session.session_id, session.lab_ip_address);
          }, 500);

        } else {
          this.recorderSession.sessionId = null;
          this.recorderSession.vncUrl    = null;
        }
      },
      error: (err: any) => {
        this.recorderSession.sessionId = null;
        this.recorderSession.vncUrl    = null;
      }
    });
  }

  createSession(): void {
    if (!this.sessionUrl || !this.selectedLab) return;

    const labIpAddress = this.selectedLab.ipAddress || this.selectedLab.labHost;
    if (!labIpAddress) {
      return;
    }

    this.isCreatingSession = true;
    this.recorderSession.sessionId = null;
    this.recorderSession.vncUrl    = null;

    this.restService.post('createRecorderSession', {
      websiteUrl:   this.sessionUrl,
      labIpAddress: labIpAddress,
      resolution:   this.selectedResolution,
      projectId:    this.getProjectId()
    }).subscribe({
      next: (res: any) => {
        const session = res.data || res;
        if (session && session.session_id) {
          this.recorderSession.sessionId = session.session_id;
          this.recorderSession.vncUrl    = session.vnc_url || null;
          // sessionUrl stays as-is — it's the URL we just opened
          setTimeout(() => {
            this.startVncClient(session.session_id, session.lab_ip_address);
          }, 500);
        }
        this.isCreatingSession = false;
      },
      error: (err: any) => {
        this.isCreatingSession = false;
      }
    });
  }

  closeSession(): void {
    this.isClosingSession = true;

    // A session closed while still recording left isRecording stuck true
    // client-side (finishClosingSession() below resets everything else,
    // but not this) — the NEXT session then opened with the UI already
    // believing a recording was in progress. Explicitly stop first so the
    // flag is correctly false before anything else about this session resets.
    if (this.recorderSession.isRecording) {
      const projectId = this.getProjectId();
      this.restService.post('stopRecorderRecording', { projectId }).subscribe({
        next: () => { this.recorderSession.isRecording = false; this.saveStepsThenClose(); },
        error: () => { this.recorderSession.isRecording = false; this.saveStepsThenClose(); }
      });
    } else {
      this.saveStepsThenClose();
    }
  }

  // "Save Steps" — persist any pending commands now, before the list resets.
  private saveStepsThenClose(): void {
    const projectId = this.getProjectId();
    if (this.stepSave.saveStepsChecked && projectId) {
      this.stepSave.saveAllStepsWithPendingCommands(projectId, 'web_recorder').subscribe({
        next: () => this.finishClosingSession(),
        error: () => this.finishClosingSession()
      });
    } else {
      this.finishClosingSession();
    }
  }

  private finishClosingSession(): void {
    this.restService.post('closeRecorderSession', { projectId: this.getProjectId() }).subscribe({
      next: () => {
        this.disconnectVnc();
        this.recorderSession.sessionId      = null;
        this.recorderSession.vncUrl         = null;
        this.recorderSession.isRecording    = false;
        this.safeVncUrl     = null;
        this.sessionUrl     = '';       // clear the shared URL field
        this.isClosingSession = false;
        this.uploadStatus   = 'idle';
        this.selectedFile   = null;
        // Explicitly ending the session — clear the persisted step list too,
        // so a future session starts fresh instead of showing stale steps.
        this.steps.reset();
      },
      error: (err: any) => {
        this.isClosingSession = false;
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // NAVIGATION — reuses sessionUrl field
  // ─────────────────────────────────────────────────────────────────────

  navigateSessionTo(openInNewTab: boolean): void {
    if (!this.sessionUrl || !this.recorderSession.sessionId) return;

    const url = this.sessionUrl.startsWith('http')
      ? this.sessionUrl
      : 'https://' + this.sessionUrl;

    const projectId = this.getProjectId();

    if (openInNewTab) {
      this.restService.post('openNewTabRecorderSession', { url, projectId }).subscribe({
        next: () => { /* sessionUrl stays so user can re-use it */ },
        error: (err: any) => console.error('New tab failed:', err)
      });
    } else {
      this.restService.post('navigateRecorderSession', { url, projectId }).subscribe({
        next: () => { this.sessionUrl = url; },
        error: (err: any) => console.error('Navigation failed:', err)
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // VNC
  // ─────────────────────────────────────────────────────────────────────

  private disconnectVnc(): void {
    if (this.rfb) {
      try {
        if (this.rfb._rfbConnectionState === 'connected') {
          this.rfb.disconnect();
        }
      } catch (e) {}
      this.rfb = null;
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  startVncClient(sessionId: string, labIpAddress: string): void {
    this.disconnectVnc();

    const password = 'selenoid';
    const url = this.buildVncWsUrl(sessionId, labIpAddress);
    this.rfb = new RFB(document.getElementById('vnc-screen'), url, {
      credentials: { password }
      
    });

    this.rfb.addEventListener('connect', () => {
      this.rfb.scaleViewport = true;
      this.rfb.resizeSession = false;
      this.rfb.clipViewport  = false;

      this.heartbeatInterval = setInterval(() => {
        if (this.rfb._sock) {
          const arr = new Uint8Array(8);
          arr[0] = 6;
          this.rfb._sock.send(arr);
        }
      }, 30000);
    });

    this.rfb.addEventListener('disconnect', (e: any) => {
      console.log(e.detail.code, e.detail.clean);
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    });
  }

  private buildVncWsUrl(sessionId: string, labIpAddress: string): string {

  if (labIpAddress.includes('ngrok')) {
    return `wss://${labIpAddress}/ws/vnc/${sessionId}`;
  }

  // Named (non-IP) lab hosts — e.g. "lab.xpeer.ai" — sit behind a TLS
  // reverse proxy on the standard port. This must be checked before the
  // "isLocal" branch below: isLocal describes whether *ataserver* is
  // running on localhost, not whether the *lab* is directly reachable —
  // a locally-run ataserver talking to a real named lab host still needs
  // wss:// on the standard port, not a raw ws://host:8080 connection
  // (which the browser can't reach and fails with an immediate,
  // uncaught-code disconnect).
  if (!this.isIpAddress(labIpAddress)) {
    return `wss://${labIpAddress}/ws/vnc/${sessionId}`;
  }

  const isLocal = environment.backendEndpoint.includes('localhost');

  if (isLocal) {
    return `ws://${labIpAddress}:8080/ws/vnc/${sessionId}`;
  }

  const backendHost = environment.backendEndpoint
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '');
  return `wss://${backendHost}:8081/${labIpAddress}/ws/vnc/${sessionId}`;
  }
  private isIpAddress(host: string): boolean {
    return /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
  }


  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files.length) {
      this.selectedFile = input.files[0];
      this.uploadStatus = 'idle';
    }
  }

  async uploadFileToSession(): Promise<void> {
    if (!this.selectedFile || !this.recorderSession.sessionId || !this.selectedLab) return;

    this.isUploading  = true;
    this.uploadStatus = 'idle';

    const formData = new FormData();
    formData.append('file', this.selectedFile);

    this.restService.postMultipart('uploadRecorderFile', formData, this.getProjectId()).subscribe({
        next: (res: any) => {
            const downloadUrl: string = res.data;
            if (!downloadUrl) {
                this.isUploading  = false;
                this.uploadStatus = 'error';
                return;
            }

            this.restService.post('downloadFileInRecorderSession', {
                url: downloadUrl,
                fileName: this.selectedFile.name,
                projectId: this.getProjectId()
            }).subscribe({
                next: () => {
                    this.isUploading  = false;
                    this.uploadStatus = 'success';
                    this.selectedFile = null;
                },
                error: (err: any) => {
                    this.isUploading  = false;
                    this.uploadStatus = 'error';
                }
            });
        },
        error: (err: any) => {
            console.error('File upload failed — status:', err.status, 'body:', err.error);
            this.isUploading  = false;
            this.uploadStatus = 'error';
        }
    });
}
}